import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useMemo, useState } from 'react';
import { ClockIcon } from '@primer/octicons-react';
import { actionStepCount } from '../../../model/detail';
import { formatChipDuration } from '../../format';
import { GenerativeIcon, PauseIcon, PlayIcon } from './CommandIcons';
import { LeafCard } from './LeafCard';
import { TimelineDot } from './TimelineDot';
import { IfElseCard, UntilCard } from './ContainerCards';
import styles from './steps.module.css';

/** Connector palette (kaneai utils.ts). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var LINE_GREEN = 'rgba(74, 194, 107, 0.40)';
var LINE_GREY = 'rgba(208, 215, 222, 0.70)';
var LINE_DEFAULT = '#d0d7de';

/** Rail bend SVGs — the line curves into an expanded block (quoted paths). */
var EntryBend = function EntryBend(_ref) {
  var color = _ref.color;
  return /*#__PURE__*/_jsxs("svg", {
    width: "24",
    height: "40",
    viewBox: "0 0 24 40",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("line", {
      x1: "12",
      y1: "0",
      x2: "12",
      y2: "28",
      stroke: color,
      strokeWidth: "1"
    }), /*#__PURE__*/_jsx("path", {
      d: "M12 28 Q12 38 22 38",
      stroke: color,
      strokeWidth: "1",
      fill: "none"
    })]
  });
};
var ExitBend = function ExitBend(_ref2) {
  var color = _ref2.color;
  return /*#__PURE__*/_jsxs("svg", {
    width: "24",
    height: "40",
    viewBox: "0 0 24 40",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M22 2 Q12 2 12 12",
      stroke: color,
      strokeWidth: "1",
      fill: "none"
    }), /*#__PURE__*/_jsx("line", {
      x1: "12",
      y1: "12",
      x2: "12",
      y2: "40",
      stroke: color,
      strokeWidth: "1"
    })]
  });
};
var Chevron = function Chevron(_ref3) {
  var expanded = _ref3.expanded;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    style: {
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 150ms'
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 4L10 8L6 12",
      stroke: "#57606A",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};
/** One child row inside a block: 24px inner rail + leaf card. */
function ChildRow(_ref4) {
  var step = _ref4.step,
    childIdx = _ref4.childIdx,
    isLast = _ref4.isLast,
    ctx = _ref4.ctx;
  var i = ctx.indexOf(step.key);
  var status = ctx.displayed(step);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.childRow,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.railColumn,
      children: [childIdx === 0 ? /*#__PURE__*/_jsx("div", {
        style: {
          height: 18
        }
      }) : /*#__PURE__*/_jsx("div", {
        className: styles.railLine,
        style: {
          height: 22,
          background: ctx.topColor(i)
        }
      }), /*#__PURE__*/_jsx(TimelineDot, {
        status: status
      }), !isLast && /*#__PURE__*/_jsx("div", {
        className: "".concat(styles.railLine, " ").concat(styles.railFill),
        style: {
          background: ctx.bottomColor(i)
        }
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.childContent,
      children: step.ifElse ? /*#__PURE__*/_jsx(IfElseCard, {
        step: step,
        ctx: ctx
      }) : step.until ? /*#__PURE__*/_jsx(UntilCard, {
        step: step,
        ctx: ctx
      }) : /*#__PURE__*/_jsx(LeafCard, {
        step: step,
        status: status,
        selected: ctx.selectedKey === step.key,
        chipHidden: ctx.chipHidden(status),
        onSelect: ctx.onSelect
      })
    })]
  });
}

/** kaneai GenerativeBlockV4: gradient-bordered card with the lavender
 * gradient header (objective + count badge) and the child rows inside. */
/** Block dot: derived for display only (autoplay-aware via children). */
function blockDotStatus(block, ctx) {
  var childStatuses = block.steps.map(function (s) {
    return ctx.displayed(s);
  });
  return childStatuses.includes('executing') ? 'executing' : childStatuses.every(function (s) {
    return s === 'autoplay_pending';
  }) ? 'autoplay_pending' : block.rollup;
}
function BlockCard(_ref5) {
  var _block$objective, _block$objective2;
  var block = _ref5.block,
    ctx = _ref5.ctx,
    expanded = _ref5.expanded,
    onToggle = _ref5.onToggle;
  var failed = block.rollup === 'failed';
  var durationSum = block.steps.reduce(function (acc, s) {
    return s.durationMs !== null ? acc + s.durationMs : acc;
  }, 0);
  var chip = formatChipDuration(durationSum > 0 ? durationSum : null);
  var borderStyle = failed ? {
    border: '1px solid #CF222E',
    borderRadius: 6
  } : {
    border: '1px solid transparent',
    borderRadius: 6,
    backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(289.41deg, #517ACD 9.63%, #7F7BA5 38.54%, #FF9A66 74.22%, #F1CBAF 98.97%)',
    backgroundOrigin: 'padding-box, border-box',
    backgroundClip: 'padding-box, border-box'
  };
  var blockDot = blockDotStatus(block, ctx);
  // Steps inside if/else branches and until cards render INSIDE their
  // container card, not as top-level block rows.
  var visibleSteps = block.steps.filter(function (s) {
    return !s.nested && !s.orphan;
  });
  return /*#__PURE__*/_jsxs("div", {
    className: styles.blockCard,
    style: borderStyle,
    role: "group",
    "aria-label": (_block$objective = block.objective) !== null && _block$objective !== void 0 ? _block$objective : 'Steps',
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      className: "".concat(styles.blockHeader, " ").concat(expanded ? styles.blockHeaderExpanded : styles.blockHeaderCollapsed),
      "aria-expanded": expanded,
      onClick: onToggle,
      children: [/*#__PURE__*/_jsx(Chevron, {
        expanded: expanded
      }), /*#__PURE__*/_jsx(GenerativeIcon, {
        status: blockDot === 'failed' ? 'block-error' : blockDot === 'passed' ? 'block-completed' : 'block-default'
      }), /*#__PURE__*/_jsx("span", {
        className: styles.blockObjective,
        title: (_block$objective2 = block.objective) !== null && _block$objective2 !== void 0 ? _block$objective2 : undefined,
        children: block.objective
      }), /*#__PURE__*/_jsx("span", {
        className: styles.blockCount,
        children: visibleSteps.length
      }), chip !== null && /*#__PURE__*/_jsxs("span", {
        className: styles.durationChip,
        children: [/*#__PURE__*/_jsx(ClockIcon, {
          size: 14,
          fill: "#636C76",
          "aria-hidden": "true"
        }), chip]
      })]
    }), expanded && /*#__PURE__*/_jsx("div", {
      className: styles.blockChildren,
      role: "list",
      children: visibleSteps.map(function (step, i) {
        return /*#__PURE__*/_jsx(ChildRow, {
          step: step,
          childIdx: i,
          isLast: i === visibleSteps.length - 1,
          ctx: ctx
        }, step.key);
      })
    })]
  });
}

/**
 * Port of AuteurStepsV4 (agentView): "N Steps" + Autoplay header, then the
 * instruction tree with the 24px timeline rail. Selection follows clicks,
 * ArrowUp/Down, and autoplay (owned by the page — the status bar in the
 * screenshot panel shares its state); the screenshot panel tracks selection
 * via the store.
 */
export function StepsPanel(_ref6) {
  var tree = _ref6.tree,
    loading = _ref6.loading,
    selectedKey = _ref6.selectedKey,
    onSelect = _ref6.onSelect,
    displayed = _ref6.displayed,
    autoplayActive = _ref6.autoplayActive,
    onToggleAutoplay = _ref6.onToggleAutoplay,
    scrollRef = _ref6.scrollRef;
  // Collapsed generative blocks — lifted here so the outer rail can swap
  // bends for a straight dot+line when a block is closed (v4 TIMELINE.md:
  // "collapsed → straight line, no bends, no spacer").
  var _useState = useState(function () {
      return new Set();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    collapsedBlocks = _useState2[0],
    setCollapsedBlocks = _useState2[1];
  var toggleBlock = function toggleBlock(key) {
    setCollapsedBlocks(function (prev) {
      var next = new Set(prev);
      if (next.has(key)) next["delete"](key);else next.add(key);
      return next;
    });
  };
  var flatSteps = useMemo(function () {
    var _tree$flatSteps;
    return (_tree$flatSteps = tree === null || tree === void 0 ? void 0 : tree.flatSteps) !== null && _tree$flatSteps !== void 0 ? _tree$flatSteps : [];
  }, [tree]);
  var indexByKey = useMemo(function () {
    var m = new Map();
    flatSteps.forEach(function (s, i) {
      return m.set(s.key, i);
    });
    return m;
  }, [flatSteps]);
  var failIdx = useMemo(function () {
    return flatSteps.findIndex(function (s) {
      return s.status === 'failed';
    });
  }, [flatSteps]);
  var ctx = {
    indexOf: function indexOf(key) {
      var _indexByKey$get;
      return (_indexByKey$get = indexByKey.get(key)) !== null && _indexByKey$get !== void 0 ? _indexByKey$get : 0;
    },
    displayed: displayed,
    topColor: function topColor(i) {
      var prev = flatSteps[i - 1];
      if (prev && displayed(prev) === 'passed') return LINE_GREEN;
      if (failIdx >= 0 && i > failIdx) return LINE_GREY;
      return LINE_DEFAULT;
    },
    bottomColor: function bottomColor(i) {
      var step = flatSteps[i];
      if (step && displayed(step) === 'passed') return LINE_GREEN;
      if (failIdx >= 0 && i >= failIdx) return LINE_GREY;
      return LINE_DEFAULT;
    },
    selectedKey: selectedKey,
    onSelect: onSelect,
    chipHidden: function chipHidden(status) {
      return status === 'executing' || status === 'autoplay_pending';
    }
  };
  var onKeyDown = function onKeyDown(event) {
    if (flatSteps.length === 0) return;
    var key = event.key;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Home' && key !== 'End') {
      return;
    }
    event.preventDefault();
    var current = flatSteps.findIndex(function (s) {
      return s.key === selectedKey;
    });
    var next;
    if (key === 'Home') next = 0;else if (key === 'End') next = flatSteps.length - 1;else if (key === 'ArrowUp') next = Math.max(0, current - 1);else next = Math.min(flatSteps.length - 1, current + 1);
    var target = flatSteps[next];
    if (target && target.key !== selectedKey) {
      var _scrollRef$current;
      onSelect(target.key);
      (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 || (_scrollRef$current = _scrollRef$current.querySelector("[data-step-key=\"".concat(CSS.escape(target.key), "\"]"))) === null || _scrollRef$current === void 0 || _scrollRef$current.focus();
    }
  };

  // Action steps only — wrappers/orphans aren't walkable rows (matches
  // the RCA drawer's "View all steps (N)").
  var count = actionStepCount(flatSteps);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.panel,
    "aria-busy": loading,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.headerRow,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.headerCount,
        children: loading ? 'Steps' : "".concat(count, " Step").concat(count === 1 ? '' : 's')
      }), count > 1 && /*#__PURE__*/_jsxs("button", {
        type: "button",
        className: styles.autoplayButton,
        onClick: onToggleAutoplay,
        children: [autoplayActive ? /*#__PURE__*/_jsx(PauseIcon, {}) : /*#__PURE__*/_jsx(PlayIcon, {}), autoplayActive ? 'Pause' : 'Autoplay']
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.scroll,
      ref: scrollRef,
      onKeyDown: onKeyDown,
      children: [loading && /*#__PURE__*/_jsx("div", {
        "aria-hidden": "true",
        className: styles.skeletons,
        children: Array.from({
          length: 6
        }, function (_, i) {
          return /*#__PURE__*/_jsxs("div", {
            className: styles.skeletonRow,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.skeletonDot
            }), /*#__PURE__*/_jsx("span", {
              className: styles.skeletonBar
            })]
          }, i);
        })
      }), !loading && (!tree || tree.source === 'empty') && /*#__PURE__*/_jsx("p", {
        className: styles.emptyNote,
        children: "This test has no step data in the pack."
      }), !loading && tree && tree.source === 'result-only' && /*#__PURE__*/_jsx("p", {
        className: styles.treeNote,
        children: "Authoring tree unavailable \u2014 showing executed steps."
      }), !loading && tree && tree.blocks.map(function (block, bi) {
        if (block.contentType === 'flat' || !block.objective) {
          // Bare steps render as top-level rows (kaneai plain cards).
          var topSteps = block.steps.filter(function (st) {
            return !st.nested && !st.orphan;
          });
          return topSteps.map(function (step, si) {
            var i = ctx.indexOf(step.key);
            var status = displayed(step);
            var isFirst = bi === 0 && si === 0;
            var isLast = bi === tree.blocks.length - 1 && si === topSteps.length - 1;
            return /*#__PURE__*/_jsxs("div", {
              className: styles.topRow,
              children: [/*#__PURE__*/_jsxs("div", {
                className: styles.railColumn,
                children: [isFirst ? /*#__PURE__*/_jsx("div", {
                  style: {
                    height: 12
                  }
                }) : /*#__PURE__*/_jsx("div", {
                  className: styles.railLine,
                  style: {
                    height: 16,
                    background: ctx.topColor(i)
                  }
                }), /*#__PURE__*/_jsx(TimelineDot, {
                  status: status
                }), !isLast && /*#__PURE__*/_jsx("div", {
                  className: "".concat(styles.railLine, " ").concat(styles.railFill),
                  style: {
                    background: ctx.bottomColor(i)
                  }
                })]
              }), /*#__PURE__*/_jsx("div", {
                className: styles.topContent,
                children: step.ifElse ? /*#__PURE__*/_jsx(IfElseCard, {
                  step: step,
                  ctx: ctx
                }) : step.until ? /*#__PURE__*/_jsx(UntilCard, {
                  step: step,
                  ctx: ctx
                }) : /*#__PURE__*/_jsx(LeafCard, {
                  step: step,
                  status: status,
                  selected: selectedKey === step.key,
                  chipHidden: ctx.chipHidden(status),
                  onSelect: onSelect
                })
              })]
            }, step.key);
          });
        }
        var firstIdx = block.steps[0] ? ctx.indexOf(block.steps[0].key) : 0;
        var lastIdx = block.steps[block.steps.length - 1] ? ctx.indexOf(block.steps[block.steps.length - 1].key) : 0;
        var isLastBlock = bi === tree.blocks.length - 1;
        var blockExpanded = !collapsedBlocks.has(block.key);
        return /*#__PURE__*/_jsxs("div", {
          className: styles.topRow,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.railColumn,
            children: [bi === 0 ? /*#__PURE__*/_jsx("div", {
              style: {
                height: 12
              }
            }) : null, blockExpanded ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(EntryBend, {
                color: ctx.topColor(firstIdx)
              }), /*#__PURE__*/_jsx("div", {
                className: styles.railSpacer
              }), !isLastBlock && /*#__PURE__*/_jsx(ExitBend, {
                color: ctx.bottomColor(lastIdx)
              })]
            }) :
            /*#__PURE__*/
            // Collapsed: the rail passes the block like any other
            // row — dot + straight connector, no bends, no spacer.
            _jsxs(_Fragment, {
              children: [bi !== 0 && /*#__PURE__*/_jsx("div", {
                className: styles.railLine,
                style: {
                  height: 16,
                  background: ctx.topColor(firstIdx)
                }
              }), /*#__PURE__*/_jsx(TimelineDot, {
                status: blockDotStatus(block, ctx)
              }), !isLastBlock && /*#__PURE__*/_jsx("div", {
                className: "".concat(styles.railLine, " ").concat(styles.railFill),
                style: {
                  background: ctx.bottomColor(lastIdx)
                }
              })]
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.topContent,
            children: /*#__PURE__*/_jsx(BlockCard, {
              block: block,
              ctx: ctx,
              expanded: blockExpanded,
              onToggle: function onToggle() {
                return toggleBlock(block.key);
              }
            })
          })]
        }, block.key);
      })]
    })]
  });
}